/* Phase E.2 — 3C : suivi d'un lot de génération multilingue.
   Le frontend est un CONSOMMATEUR de la vérité backend : statuts, compteurs,
   progression, raisons et éligibilité viennent exclusivement de l'API. */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Loader2, RotateCcw } from "lucide-react";

export const TERMINAL_BATCH_STATUSES = [
  "COMPLETED", "COMPLETED_WITH_ERRORS", "FAILED", "INTERRUPTED",
];

export const BATCH_STATUS_LABELS = {
  CREATED: "En attente",
  RUNNING: "En cours",
  COMPLETED: "Terminé",
  COMPLETED_WITH_ERRORS: "Terminé avec erreurs",
  FAILED: "Échec",
  INTERRUPTED: "Interrompu",
};

export const BATCH_STATUS_STYLES = {
  CREATED: "bg-slate-100 text-slate-600",
  RUNNING: "bg-blue-100 text-blue-700 animate-pulse",
  COMPLETED: "bg-emerald-100 text-emerald-700",
  COMPLETED_WITH_ERRORS: "bg-amber-100 text-amber-700",
  FAILED: "bg-red-100 text-red-700",
  INTERRUPTED: "bg-orange-100 text-orange-700",
};

export const ITEM_STATUS_LABELS = {
  PENDING: "En attente",
  RUNNING: "En cours",
  SUCCESS: "Réussi",
  ERROR: "Erreur",
  SKIPPED: "Ignoré",
  INTERRUPTED: "Interrompu",
};

export const ITEM_STATUS_STYLES = {
  PENDING: "bg-slate-100 text-slate-500",
  RUNNING: "bg-blue-100 text-blue-700 animate-pulse",
  SUCCESS: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  ERROR: "bg-red-50 text-red-700 border border-red-200",
  SKIPPED: "bg-slate-50 text-slate-500 border border-slate-200",
  INTERRUPTED: "bg-orange-50 text-orange-700 border border-orange-200",
};

// Codes backend RÉELS conservés en interne — seul le libellé est traduit pour l'humain.
export const BATCH_REASON_LABELS = {
  GENERATION_IN_PROGRESS: "Une génération est déjà en cours",
  LOCALE_MODIFIED_SINCE_PREVIEW: "Le contenu a été modifié après la prévisualisation. Il n'a pas été écrasé.",
  STATUS_NOT_ELIGIBLE: "Statut non compatible avec cette action",
  LOCALE_DISABLED: "Langue désactivée",
  QUOTA_EXCEEDED: "Quota actuellement insuffisant",
  LOCALE_NOT_INITIALIZED: "Langue non initialisée pour ce contenu",
  PARENT_NOT_FOUND: "Contenu introuvable",
  SITE_NOT_FOUND: "Site introuvable",
  OWNERSHIP_LOST: "Propriété de la génération perdue — aucun contenu écrasé",
  PROCESS_RESTART: "Interrompu par un redémarrage du service",
};

export const OPERATION_LABELS = {
  generate_missing: "Générer les langues manquantes",
  regenerate: "Régénérer les contenus existants",
};

export function genIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function reasonLabel(code) {
  if (!code) return null;
  return BATCH_REASON_LABELS[code] || code;
}

/* Erreur normalisée : jamais de stack/traceback/secret affiché brut. */
export function normalizeError(msg) {
  if (!msg) return null;
  const first = String(msg).split("\n")[0].trim();
  return first.length > 300 ? `${first.slice(0, 300)}…` : first;
}

export function batchSummary(batch) {
  if (!batch || !TERMINAL_BATCH_STATUSES.includes(batch.status)) return null;
  const c = batch.counts || {};
  const s = c.success || 0, e = c.error || 0, k = c.skipped || 0, i = c.interrupted || 0;
  if (batch.status === "COMPLETED" && s === 0 && k > 0) {
    return `Terminé — aucun contenu généré, ${k} élément${k > 1 ? "s" : ""} ignoré${k > 1 ? "s" : ""}`;
  }
  const parts = [`${s} réussite${s > 1 ? "s" : ""}`, `${k} ignorée${k > 1 ? "s" : ""}`,
                 `${e} erreur${e > 1 ? "s" : ""}`];
  if (i > 0) parts.push(`${i} interrompue${i > 1 ? "s" : ""}`);
  return parts.join(" · ");
}

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function BatchProgressPanel({ batchId, parentsById = {}, readOnly = false,
                                             onTerminal, onRetryLaunched }) {
  const [batch, setBatch] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const timerRef = useRef(null);
  const terminalNotifiedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setBatch(null);
    terminalNotifiedRef.current = false;
    const tick = async () => {
      let data = null;
      try {
        const res = await api.get(`/content-batches/${batchId}`);
        data = res.data;
      } catch {
        data = null;
      }
      if (cancelled) return;
      if (data) {
        setBatch(data);
        if (TERMINAL_BATCH_STATUSES.includes(data.status)) {
          if (!terminalNotifiedRef.current) {
            terminalNotifiedRef.current = true;
            onTerminal?.(data);
          }
          return; // arrêt immédiat du polling en état terminal
        }
      }
      timerRef.current = setTimeout(tick, 2500); // polling seulement CREATED/RUNNING
    };
    tick();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [batchId]); // nettoyage au changement de lot / unmount

  const retry = useCallback(async () => {
    setRetrying(true);
    try {
      // relance = NOUVELLE demande → nouvelle clé d'idempotence
      const { data } = await api.post(`/content-batches/${batchId}/retry-failed`,
                                      { idempotency_key: genIdempotencyKey() });
      toast.success("Relance des échecs lancée");
      onRetryLaunched?.(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "NO_RETRYABLE_ITEMS") {
        toast.info("Aucun élément à relancer.");
      } else {
        toast.error(normalizeError(typeof d === "string" ? d : d?.message) || "Relance impossible");
      }
    } finally {
      setRetrying(false);
    }
  }, [batchId, onRetryLaunched]);

  if (!batch) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 p-4" data-testid="content-batch-detail-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement du lot…
      </div>
    );
  }

  const c = batch.counts || {};
  const done = batch.done ?? ((c.success || 0) + (c.error || 0) + (c.skipped || 0) + (c.interrupted || 0));
  const total = batch.total || 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const terminal = TERMINAL_BATCH_STATUSES.includes(batch.status);
  const retryable = (c.error || 0) + (c.interrupted || 0) > 0;
  const summary = batchSummary(batch);

  return (
    <div className="space-y-4" data-testid="content-batch-detail">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate-900">
            {OPERATION_LABELS[batch.operation_type] || batch.operation_type}
          </div>
          <div className="text-[11px] text-slate-500">
            Créé le {formatDate(batch.created_at)}
            {batch.finished_at ? ` · terminé le ${formatDate(batch.finished_at)}` : ""}
          </div>
        </div>
        <span data-testid="content-batch-status"
          className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${BATCH_STATUS_STYLES[batch.status] || "bg-slate-100 text-slate-600"}`}>
          {BATCH_STATUS_LABELS[batch.status] || batch.status}
        </span>
      </div>

      <div data-testid="content-batch-progress" aria-label={`Progression : ${done} sur ${total}`}>
        <div className="flex items-center justify-between text-[11px] text-slate-600 mb-1">
          <span>{done} / {total} traités</span>
          <span className="flex items-center gap-2 flex-wrap">
            <span className="text-emerald-700">{c.success || 0} réussi{(c.success || 0) > 1 ? "s" : ""}</span>
            <span className="text-red-700">{c.error || 0} en erreur</span>
            <span className="text-slate-500">{c.skipped || 0} ignoré{(c.skipped || 0) > 1 ? "s" : ""}</span>
            <span className="text-orange-700">{c.interrupted || 0} interrompu{(c.interrupted || 0) > 1 ? "s" : ""}</span>
          </span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-[#002FA7] rounded-full transition-[width] duration-500"
               style={{ width: `${pct}%` }} />
        </div>
      </div>

      {terminal && summary && (
        <div data-testid="content-batch-summary"
          className={`text-sm rounded-md px-3 py-2 border ${
            batch.status === "COMPLETED" ? "bg-emerald-50 border-emerald-200 text-emerald-800"
            : batch.status === "FAILED" ? "bg-red-50 border-red-200 text-red-800"
            : "bg-amber-50 border-amber-200 text-amber-800"}`}>
          {batch.status === "INTERRUPTED" ? "Lot interrompu — " : ""}{summary}
          {batch.status === "INTERRUPTED" && (
            <span className="block text-[11px] mt-1">
              {" "}La reprise n&apos;est jamais automatique — utilisez « Relancer les échecs » si besoin.
            </span>
          )}
        </div>
      )}

      <div className="space-y-1.5" data-testid="content-batch-items">
        {(batch.items || []).map((it) => {
          const parent = parentsById[it.parent_id];
          const err = normalizeError(it.error);
          return (
            <div key={it.id || `${it.parent_id}-${it.locale}`}
              data-testid={`content-batch-item-${it.parent_id}-${it.locale}`}
              data-item-status={it.status} data-item-reason={it.reason || ""}
              className="flex items-start gap-2 text-xs border border-slate-100 rounded-md px-2.5 py-1.5 bg-white">
              <span className="font-mono font-semibold text-slate-700 shrink-0">{it.locale}</span>
              <span className="text-slate-500 truncate flex-1 min-w-0">
                {parent?.topic || it.parent_id}
              </span>
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 ${ITEM_STATUS_STYLES[it.status] || "bg-slate-100"}`}>
                {ITEM_STATUS_LABELS[it.status] || it.status}
              </span>
              {it.reason && (
                <span className="text-[10px] text-slate-500 shrink-0 max-w-[45%] truncate"
                      title={it.reason}>
                  {reasonLabel(it.reason)}
                </span>
              )}
              {err && !it.reason && (
                <span className="text-[10px] text-red-600 shrink-0 max-w-[45%] truncate" title={err}>
                  {err}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {terminal && retryable && !readOnly && (
        <button onClick={retry} disabled={retrying} data-testid="content-batch-retry"
          className="inline-flex items-center gap-2 text-sm font-medium text-[#002FA7] border border-[#002FA7]/30 rounded-md px-3 py-1.5 hover:bg-blue-50 disabled:opacity-50">
          {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
          Relancer les échecs
        </button>
      )}
      {batch.status === "FAILED" && !retryable && (
        <div className="flex items-center gap-2 text-xs text-red-700">
          <AlertTriangle className="w-4 h-4" /> Aucun élément relançable.
        </div>
      )}
    </div>
  );
}
